import * as React from 'react';
import * as moment from 'moment';
import uniq = require('lodash/uniq');

import { IEventFilter, IDay, IOption, IMonth } from '../../classes/buytickets';
import { IEvent } from '../../classes/event';
import { upsertEventFilter, removeEventFilter } from '../../redux-state/actions/buytickets.actions';
import { IBuyTicketsAppState } from '../../redux-state';
import CalendarApi from '../../common/buytickets-api';
import { connect } from 'react-redux';
import { generateEventFilter } from '../../helpers/filter-by-query'
import MultiSelectFilterView from '../../views/multi-select-filter-view';

interface IFilterExternalProps {
    /**
     * The property to filter the calendar events by.
     */
    propertyToFilter: keyof IEvent;
    /**
     * Text to represent the option for no selection. ie All Venues, All Production
     */
    noSelectionText: string;
    /**
     * Name of the filter to be labeled on the app
     */
    filterName: string;
}

interface StateToProps {
    hasFilters: boolean;
    events: IEvent[];
    selectedValue: string;
    /**
     * The furthermost month that has been loaded onto the calendar.
     */
    lastLoadedMonth: moment.Moment;
    /**
     * The last month that can be loaded onto the calendar.
     */
    lastMonthToLoad: moment.Moment;
}

interface DispatchToProps {
    selectFilter: (selectedValue: string) => void;
}

export type FilterProps = IFilterExternalProps & StateToProps & DispatchToProps;

interface FilterState {
    fromApi: boolean;
    waitingForApi: boolean;
    options: IOption[];
}

export type CombinedFilterViewProps = FilterProps & FilterState;

export class FiltersBase extends React.Component<FilterProps, FilterState> {
    constructor(props: FilterProps) {
        super(props);
         
        this.state = {
            fromApi: false,
            waitingForApi: true,
            options: []
        };
    }

    componentDidMount() {
        CalendarApi.getFiltersFor(this.props.propertyToFilter).then(response => {
            this.setState({
                fromApi: true,
                waitingForApi: false,
                options: response
            });
        }).catch(err => {
            console.error(err);
        });
    }

    componentDidUpdate(prevProps: FilterProps, prevState: FilterState) {
        if (!this.state.fromApi && !this.state.waitingForApi && !prevProps.lastLoadedMonth.isSame(this.props.lastLoadedMonth)) {
            this.setState({
                options: this.calculateOptions(this.props)
            });
        }
    }

    componentWillUnmount() {
        CalendarApi.cancelFiltersRequest();
    }

    private calculateOptions(props: FilterProps): IOption[] {
        if (props.events.length > 0) {
            console.log(props.events[0][props.propertyToFilter]);
            if (Array.isArray(props.events[0][props.propertyToFilter])) {
                // These are all casted as any because it's not specifically known what kind of objects this is expected to support.
                const allOptions = uniq(([] as any[]).concat(...props.events.map(event => (event[props.propertyToFilter] as any[]))));

                return allOptions.map(option => {
                    return {
                        text: option.toString(),
                        value: option.toString(),
                        firstOccurance: ""
                    }
                });
            }
            else {
                const mappedProperty = uniq(props.events.map(event => event[props.propertyToFilter]));

                return mappedProperty.map(eventProperty => {
                    return {
                        text: eventProperty.toString(),
                        value: eventProperty.toString(),
                        firstOccurance: ""
                    }
                });
            }
        }

        // If empty
        return [];
    }

    render() {
        return <MultiSelectFilterView {...this.props} {...this.state} />;
    }
}

function mapStateToProps(state: IBuyTicketsAppState, ownProps: IFilterExternalProps): StateToProps {
    // Find the matched filter to extract the selected value.
    let matchedFilter: IEventFilter = null;

    for (let i = 0; i < state.calendar.eventFilter.length; ++i) {
        if (state.calendar.eventFilter[i].key === ownProps.propertyToFilter) {
            matchedFilter = state.calendar.eventFilter[i];
            break;
        }
    }

    return {
        hasFilters: state.calendar.eventFilter.length > 0,
        events: state.calendar.events,
        selectedValue: matchedFilter ? matchedFilter.selectedValue : "",
        lastLoadedMonth: state.calendar.endDate,
        lastMonthToLoad: state.calendarSettings.monthsOnCalendar[state.calendarSettings.monthsOnCalendar.length - 1]
    }
}

function mapDispatchToProps(dispatch, ownProps: FilterProps): DispatchToProps {
    return {
        selectFilter: (selectedValue: string) => {
            // If the selected value is null or empty string, remove that filter.
            if (!selectedValue || selectedValue === "") {
                dispatch(removeEventFilter(ownProps.propertyToFilter));
            } else {

                const filter: IEventFilter = {
                    key: ownProps.propertyToFilter,
                    selectedValue: selectedValue,
                    filter: generateEventFilter(ownProps.propertyToFilter, selectedValue)
                }

                dispatch(upsertEventFilter(filter));
              
            }
        }
    }
}

export default connect<StateToProps, DispatchToProps>(mapStateToProps, mapDispatchToProps)(FiltersBase);